import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import StudentPortal from "./pages/StudentPortal";
import StudentDashboard from "./pages/StudentDashboard";
import CoordinatorDashboard from "./pages/CoordinatorDashboard";
import PrincipalDashboard from "./pages/PrincipalDashboard";
import VicePrincipalDashboard from "./pages/VicePrincipalDashboard";
import WardenDashboard from "./pages/WardenDashboard";
import GateOperatorDashboard from "./pages/GateOperatorDashboard";
import ReceptionDashboard from "./pages/ReceptionDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import PrintGatePass from "./pages/PrintGatePass";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const RoleBasedRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  switch (user.role) {
    case "student":
      return <Navigate to="/student" replace />;
    case "coordinator":
      return <Navigate to="/coordinator" replace />;
    case "principal":
      return <Navigate to="/principal" replace />;
    case "vice_principal":
      return <Navigate to="/vice-principal" replace />;
    case "hostel_warden":
      return <Navigate to="/warden" replace />;
    case "gate_operator":
      return <Navigate to="/gate" replace />;
    case "reception":
      return <Navigate to="/reception" replace />;
    case "admin":
      return <Navigate to="/admin" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<RoleBasedRedirect />} />
          
          {/* Public Student Portal - No Login Required */}
          <Route path="/portal" element={<StudentPortal />} />
          
          <Route path="/student/*" element={
            <ProtectedRoute allowedRoles={["student"]}>
              <StudentDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/coordinator/*" element={
            <ProtectedRoute allowedRoles={["coordinator"]}>
              <CoordinatorDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/principal/*" element={
            <ProtectedRoute allowedRoles={["principal"]}>
              <PrincipalDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/vice-principal/*" element={
            <ProtectedRoute allowedRoles={["vice_principal"]}>
              <VicePrincipalDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/warden/*" element={
            <ProtectedRoute allowedRoles={["hostel_warden"]}>
              <WardenDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/gate/*" element={
            <ProtectedRoute allowedRoles={["gate_operator"]}>
              <GateOperatorDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/reception/*" element={
            <ProtectedRoute allowedRoles={["reception"]}>
              <ReceptionDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/*" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/print/:passId" element={
            <ProtectedRoute>
              <PrintGatePass />
            </ProtectedRoute>
          } />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
